<template>
  <div v-if="hasPermission('members.clients.view')">
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :iconHeader="hasPermission('members.clients.add') ? 'add' : null"
      :items="displayedAssignedUsers"
      :itemActions="actions"
      :headers="tableHeaders"
      :title="$t('assignedUsers.pageName')"
      :pagination="pagination"
      :isInfinitScroll="true"
      :isSearchable="false"
      :isCustomIcon="true"
      :centerColumns="['action']"
      :sortableColumns="['firstname', 'lastname']"
      :notSortableColumns="['action', 'email', 'memberSince']"
      startIconName="person_outline"
      hideDefaultFooter
      isSelectable
      @sendQueryParams="fetchQuery"
      @send-item="onClickRedirect"
      @iconAction="onClickAction"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
  <div v-else>
    <NoPermissionAlert :message="$t('permissions.error')" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import AssociateMemberForm from '@/components/clients/AssociateMemberForm';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import roles from '@/utilities/enums/rolesTypes';


export default {
    components: {
        TableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            assignedUsers: [],
            loading: false,
            pagination: {},
            queryParams: { onlyExplicit: true, includeInvited: true },
            isInitializing: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                'firstName',
                'lastName',
                'email',
                'memberSince',
            ].map(header => ({
                text: this.$t(`members.labels.${header}`),
                value: header,
            }));
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });
            return headers;
        },
        displayedAssignedUsers() {
            return this.assignedUsers.map(member => ({
                ...member,
                memberSince: this.$options.filters.formatDate(member.memberSince, 'l'),
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('members.clients.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getAssignedUsers();
        this.loading = false;
    },
    methods: {
        async getAssignedUsers() {
            this.isInitializing = true;
            const id = this.$route.params.clientId;
            const { data } = await this.$rq.getClientMembers(id, this.queryParams);
            this.assignedUsers = [...this.assignedUsers, ...data.members.filter(member => member.roles[0].key !== roles.OPERATOR)];
            this.pagination = data?.pagination;
            // we filter the user so the pagination needs to recalculate
            if (data.members.length !== this.assignedUsers.length) {
                this.pagination.totalElementCount = this.assignedUsers.length;
            }
            this.isInitializing = false;
        },
        async fetchQuery(queryParams) {
            if (!this.isInitializing) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.assignedUsers = [];
                await this.getAssignedUsers();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;

            switch (name) {
                case 'firstname':
                    newValue = 'firstName';
                    break;
                case 'lastname':
                    newValue = 'lastName';
                    break;
                case 'email':
                    newValue = 'email';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('members.associateMember'),
                payload: { associatedMembers: this.assignedUsers },
                component: Vue.component('associateMemberForm', AssociateMemberForm),
                onConfirm: requestPayload => this.addMemberRequest(requestPayload),
            });
        },
        onClickRedirect({ id }) {
            this.$router.push({
                name: 'assignedUser',
                params: {
                    memberId: id,
                },
            });
        },
        async addMemberRequest(requestPayload) {
            const { clientId } = this.$route.params;
            await this.$rq.associateMemberToClient(requestPayload, { clientId });
            this.$toasted.global.toast_success({
                message: this.$t('msg.memberAssociated'),
            });
            this.fetchQuery({});
        },
        onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('members.revokeClient'),
                    component: Vue.component('revokeForm', ConfirmForm),
                    payload: {
                        msg: this.$t('members.confirmRevoke', {
                            client: 'this client',
                            member: `${item.firstName} ${item.lastName}`,
                        }),
                    },
                    onConfirm: () => this.removeMemberFromClient(item),
                    isForm: false,
                });
            }
        },
        async removeMemberFromClient({ id: memberId, firstName, lastName }) {
            //
            const { clientId } = this.$route.params;
            await this.$rq.revokeClientToMember(memberId, clientId);
            this.$toasted.global.toast_success({
                message: this.$t('msg.memberRevoked', {
                    member: `${firstName} ${lastName}`,
                }),
            });
            this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getAssignedUsers();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
